import { Accordion, AccordionDetails, AccordionSummary, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { DateTime } from 'luxon';
import type { FC, PropsWithChildren } from 'react';
import { formatDateRange, formatTimeRange, isMatchingDay } from '../../util';
import { FormatDate } from '../format';
import { Icon } from '../icons';

interface JourneyAccordionProps extends PropsWithChildren {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, expanded: boolean) => void;
  startTime: Date;
  endTime: Date;
}

export const JourneyAccordion: FC<JourneyAccordionProps> = ({ expanded, onChange, startTime, endTime, children }) => {
  const startDateTime = DateTime.fromJSDate(startTime);
  const endDateTime = DateTime.fromJSDate(endTime);

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={onChange}
    >
      <StyledAccordionSummary
        expandIcon={<Icon icon="ChevronDown" />}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          py={1}
          pl={1}
        >
          <Typography color="text.secondary" fontSize="small">
            {!isMatchingDay(startDateTime, endDateTime) && (endTime > startTime)
              ? formatDateRange(startDateTime, endDateTime)
              : <FormatDate value={startTime} />
            }
          </Typography>
          <Typography>
            {formatTimeRange(startTime, endTime)}
          </Typography>
        </Stack>
      </StyledAccordionSummary>
      <AccordionDetails>
        {children}
      </AccordionDetails>
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: 'unset',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': { minHeight: 'fit-content', padding: 0 },
  '& .MuiAccordionSummary-root.Mui-expanded': { minHeight: 'fit-content' },
  '& .MuiAccordionDetails-root': { padding: `${theme.spacing(1)} 0` },
  boxShadow: 'none',
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  '& .MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    margin: '0',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '0',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
});
